<script setup lang="ts">
import { getPartnerBySlug } from "~/backend/composables/base"
import("~/assets/styles/partners.scss")

const route = useRoute()
const slug = String(route.params.slug)
const partner = await getPartnerBySlug(slug)

const seo = partner.value?.seo
useSeoMeta({
  title: seo?.metaTitle,
  ogTitle: seo?.metaTitle,
  description: seo?.metaDescription,
  ogDescription: seo?.metaDescription,
  keywords: seo?.keywords,
  robots: seo?.metaRobots,
  ogImageUrl:
    useStrapiMediaUrl(seo?.metaImage?.data?.attributes?.url) || "/og-image.jpg",
})

const formatContent = (content: string) => {
  const replacements = [
    ["</h1>", "</div>"],
    ["</h2>", "</div>"],
    ["</h3>", "</div>"],
    ["</h4>", "</div>"],
    ["</h5>", "</div>"],
    ["</h6>", "</div>"],
    ["<h1", "<div class='h1'"],
    ["<h2", "<div class='h2'"],
    ["<h3", "<div class='h3'"],
    ["<h4", "<div class='h4'"],
    ["<h5", "<div class='h5'"],
    ["<h6", "<div class='h6'"],
  ]

  return replacements.reduce((acc, [oldStr, newStr]) => {
    return acc.replaceAll(oldStr, newStr)
  }, content)
}

const { $gsap, $ScrollTrigger } = useNuxtApp()

const scope = ref<HTMLElement>()
const ctx = ref<gsap.Context>()
const tl = ref<gsap.core.Timeline>()

onMounted(() => {
  const vw = window.innerWidth

  ctx.value = $gsap.context(() => {
    tl.value = $gsap.timeline({
      defaults: {
        scrollTrigger: {
          immediateRender: false,
          invalidateOnRefresh: true,
          toggleActions: "restart none none reverse",
        },
      },
    })

    $gsap.utils
      .toArray<gsap.TweenTarget>(".advantages__row-item")
      ?.forEach((el) => {
        tl.value?.from(el, {
          scale: 0.8,
          yPercent: 50,
          opacity: 0.5,
          scrollTrigger: {
            trigger: el as gsap.DOMTarget,
            scrub: 1,
            pin: false,
            start: "top 85%",
            end: "+=300",
          },
        })
      })

    $gsap.utils.toArray<HTMLElement>(".image__wrapper")?.forEach((el) => {
      tl.value?.from(el.querySelector("img"), {
        scale: 1.2,
        yPercent: -20,
        scrollTrigger: {
          trigger: el as gsap.DOMTarget,
          scrub: 1,
          pin: false,
          start: "top bottom",
          // end: "+=300",
        },
      })
    })

    tl.value?.fromTo(
      ".transition .transition-bg",
      {
        scaleY: 0,
        scaleX: 1,
      },
      {
        scaleY: 8,
        scaleX: 3,
        // opacity: 0,
        scrollTrigger: {
          trigger: ".transition__wrapper",
          scrub: 0.1,
          pin: true,
          pinSpacing: false,
          // start: "top top",
          end: "+=1600",
        },
      },
    )

    $gsap.utils
      .toArray<gsap.DOMTarget>(".page__wrapper-black .h2 strong")
      ?.forEach((el) => {
        tl.value?.to(el, {
          backgroundImage:
            "linear-gradient(45deg, #fff 100%, #fff 200%, #fff 300%)",
          duration: 7,
          scrollTrigger: {
            trigger: el,
            start: "top 80%",
            scrub: true,
          },
        })
      })

    const advancedItems = $gsap.utils.toArray<HTMLElement>(
      ".advanced__row-item",
    )
    $gsap.set(advancedItems, {
      yPercent: 50,
      opacity: 0,
    })
    $gsap?.to(advancedItems, {
      yPercent: 0,
      opacity: 1,
      stagger: 0.3,
      duration: 1,
      scrollTrigger: {
        trigger: ".advanced__row",
        start: "top 90%",
        end: "+=800",
        // scrub: true,
        toggleActions: "restart none none none",
      },
    })

    const galleryContainer =
      scope.value?.querySelector<HTMLElement>(".gallery__row")
    const galleryItems = $gsap.utils.toArray(".gallery__row-item")
    const galleryScroll =
      (galleryContainer?.offsetWidth || 0) - window.innerWidth

    tl.value?.to(galleryContainer as gsap.TweenTarget, {
      x: `-${galleryScroll}`,
      // ease: "none",
      scrollTrigger: {
        trigger: ".gallery__wrapper",
        start: "top 10%",
        end: `+=${galleryScroll}`,
        scrub: true,
        pin: true,
        snap: 1 / (galleryItems.length - 1),
        onUpdate({ getVelocity }) {
          $gsap.fromTo(
            galleryContainer as gsap.TweenTarget,
            {
              skewX: `${getVelocity() / 800}deg`,
            },
            {
              skewX: 0,
            },
          )
        },
      },
    })

    const servicesContainer =
      scope.value?.querySelector<HTMLElement>(".services__row")

    if (
      servicesContainer &&
      servicesContainer.getBoundingClientRect().width < vw
    ) {
      servicesContainer.classList.add("services__row--center")
    }

    const servicesItems = $gsap.utils.toArray(".services__row-item")
    const servicesScroll =
      (servicesContainer?.offsetWidth || 0) - window.innerWidth

    tl.value?.to(servicesContainer as gsap.TweenTarget, {
      x: `-${servicesScroll}`,
      scrollTrigger: {
        trigger: ".services__wrapper",
        start: "bottom bottom-=50",
        end: `+=${servicesScroll}`,
        scrub: true,
        pin: true,
        snap: 1 / (servicesItems.length - 1),
      },
    })
  }, scope.value)
})
onUnmounted(() => {
  $ScrollTrigger.getAll()?.forEach((t) => t.kill())
  ctx.value?.revert()
})
onBeforeMount(() => {
  $ScrollTrigger.getAll()?.forEach((t) => t.kill())
})
</script>

<template>
  <div ref="scope">
    <PageWrapper v-if="partner" class="page--partner">
      <HeaderBlock />
      <section class="page__top">
        <span class="flare flare-purple" />
        <span class="flare flare-blue" />
        <div class="section__wrapper section__wrapper-medium">
          <img
            v-if="partner.logo.data"
            :src="useStrapiMediaUrl(partner.logo.data.attributes.url)"
            class="top-logo"
            loading="lazy"
          />
          <div class="text-44 text-bold top-title" v-html="partner.company" />
          <p class="text-20 top-description" v-html="partner.aboutCompany" />
        </div>
        <div class="top-circle-wrapper">
          <img src="/img/partner_circle.svg" alt="" class="circle" />
          <img src="/img/award_icon.svg" alt="" class="icon" />
        </div>
      </section>
      <article class="page__wrapper">
        <section v-if="partner.description" class="page__about">
          <div class="section__wrapper">
            <div class="h2 heading-thin" v-html="partner.description" />
          </div>
          <div
            v-if="partner?.photo?.data?.attributes?.url"
            class="image__wrapper image__wrapper-fullscreen"
          >
            <img
              :src="useStrapiMediaUrl(partner.photo.data.attributes.url)"
              alt=""
              loading="lazy"
            />
          </div>
        </section>
        <section v-if="partner?.advantages" class="page__advantages">
          <div class="section__wrapper">
            <div class="advantages__row">
              <div
                v-for="(item, id) of partner.advantages"
                :key="id"
                class="advantages__row-item"
              >
                <div class="h4">{{ item.heading }}</div>
                <div class="content" v-html="item.content" />
              </div>
            </div>
            <div v-if="partner.video" class="video__wrapper">
              <div class="video__container" v-html="partner.video" />
              <span class="flare flare-blue" />
            </div>
          </div>
        </section>
        <div v-if="partner.blackContent" class="transition">
          <div class="transition__wrapper">
            <img src="/img/transition.png" alt="" class="transition-bg" />
          </div>
        </div>
      </article>
      <article
        v-if="partner.blackContent"
        class="page__wrapper page__wrapper-black"
      >
        <section class="page__advanced">
          <span class="flare flare-purple" />
          <div class="section__wrapper">
            <div
              class="html__content advanced__content"
              v-html="formatContent(partner.blackContent)"
            />
            <div v-if="partner.textItems" class="advanced__row">
              <div
                v-for="(item, id) of partner.textItems"
                :key="id"
                class="advanced__row-item"
              >
                <div class="h4" v-html="item.content" />
              </div>
            </div>
          </div>
        </section>
        <section v-if="partner.gallery?.data" class="page__gallery">
          <div class="gallery__wrapper">
            <div class="gallery__row">
              <div
                v-for="(item, id) of partner.gallery.data"
                :key="id"
                class="gallery__row-item"
              >
                <img
                  :src="useStrapiMediaUrl(item.attributes.formats.medium.url)"
                  alt=""
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
      </article>
      <article class="page__wrapper">
        <section v-if="partner.secondWhiteContent" class="page__content">
          <div class="section__wrapper section__wrapper-medium">
            <div
              class="html__content"
              v-html="formatContent(partner.secondWhiteContent)"
            />
          </div>
        </section>
        <section v-if="partner.services" class="page__services">
          <div class="services__wrapper">
            <div class="services__row">
              <div
                v-for="(item, id) of partner.services"
                :key="id"
                class="services__row-item"
              >
                <div class="img-wrapper">
                  <img
                    v-if="item.image"
                    :src="
                      useStrapiMediaUrl(
                        item.image.data.attributes.formats.medium.url,
                      )
                    "
                    class="image"
                    alt=""
                    loading="lazy"
                  />
                </div>
                <div class="h3 title" v-html="item.name" />
                <div class="content html__content" v-html="item.content" />
                <a
                  v-if="item.link"
                  :href="item.link"
                  class="button button--primary button--large"
                  target="_blank"
                  rel="nofollow"
                >
                  Подробнее
                </a>
              </div>
            </div>
          </div>
        </section>
        <section v-if="partner.thirdWhiteContent" class="page__content">
          <div class="section__wrapper section__wrapper-medium">
            <div
              class="html__content"
              v-html="formatContent(partner.thirdWhiteContent)"
            />
          </div>
        </section>
        <section class="page__button">
          <div class="section__wrapper section__wrapper-medium">
            <a
              v-if="partner.button"
              :href="partner.button.link"
              class="button button--primary button--large"
              target="_blank"
              rel="nofollow"
            >
              {{ partner.button?.text }}
            </a>
          </div>
        </section>
      </article>
      <FooterBlock />
    </PageWrapper>
  </div>
</template>

<style lang="scss" scoped>
.transition {
  position: relative;
  pointer-events: none;
  z-index: 3;

  &__wrapper {
    width: 100vw;
    height: 100vh;
    margin-top: -50vh;
  }
  &-bg {
    display: block;
    height: 100%;
    width: 100%;
    max-width: 100%;
    transform-origin: bottom center;
    pointer-events: none;
  }
}
.flare {
  --translate-x: -50%;
  --translate-y: -50%;
  --rotate: 20deg;
  --width: 100vw;
  --height: 100vw;
  position: absolute;
  left: 0;
  top: 0;
  transform: translate(var(--translate-x), var(--translate-y))
    rotate(var(--rotate));
  width: var(--width);
  height: var(--height);
  border-radius: 999px;
  user-select: none;

  &-purple {
    background: radial-gradient(
      rgba(106, 69, 255, 100%) 0%,
      rgba(106, 69, 255, 0) 70%
    );
  }
  &-blue {
    background: radial-gradient(
      rgba(72, 26, 255, 1) 0%,
      rgba(72, 26, 255, 0) 70%
    );
  }
}
.h1,
.h2,
.h3,
.h4,
.h5,
.h6 {
  font-weight: 700;
}
.h2 {
  font-size: 1.5em;
}
.page {
  line-height: 1.4;
  overflow: hidden;

  &__wrapper {
    position: relative;
    background: url("/img/bg_reviews.png") no-repeat left top/30%;
    background-color: #fff;
    padding: 17vh 0;
    border-radius: 20px;
    color: #4c4c4c;
    z-index: 3;
    margin-bottom: -5vh;

    // .h2{
    //   font-weight: 400;
    // }
    &-black {
      background: none;
      color: rgba(255, 255, 255, 0.7);
      padding-top: 5vh;
      border-radius: none;
      overflow: hidden;
    }
    section:not(:last-of-type) {
      margin-bottom: 15vh;

      @include sm {
        margin-bottom: 4rem;
      }
    }
    @include sm {
      padding: 4rem 0;
      background-size: 50%;
    }
  }
  .section__wrapper {
    max-width: 1000px;
    padding-left: 16px;
    padding-right: 16px;

    &-small {
      max-width: 640px;
    }
    &-medium {
      max-width: 820px;
    }
    &-large {
      max-width: 1300px;
    }
  }
  .heading-thin {
    font-weight: 400;

    @include sm {
      font-size: 1.125rem;
    }
  }
  .image__wrapper {
    overflow: hidden;
    margin-top: var(--margin-top, 12vh);

    img {
      display: block;
      width: 100%;
      height: 100%;
      max-width: 100%;
      object-fit: cover;
      object-position: center center;
    }
    &-fullscreen {
      width: 100%;
      height: 100vh;
    }
    &-fullwidth {
      width: 100%;
      aspect-ratio: 16/9;
    }
    @include sm {
      height: auto;
      aspect-ratio: 3/4;
      margin-top: 4rem;
    }
  }
  .video__wrapper {
    position: relative;
    margin-top: 5rem;

    .video__container {
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .flare {
      --width: 40vw;
      --height: 40vw;
      opacity: 0.2;
      z-index: -1;
      top: unset;
      animation: pulse 8s infinite ease-in-out;
    }
    @include sm {
      margin-top: 4rem;
    }
  }
  &__top {
    position: relative;
    padding: 23vh 0 20vh 0;
    text-align: center;
    overflow: hidden;

    .top-title {
      color: #fff;
      line-height: 1.2;
      margin-bottom: 0.45em;
    }
    .top-description {
      color: rgba(255, 255, 255, 0.7);
      line-height: 1.4;

      @include sm {
        font-size: 14px;
      }
    }
    .top-logo {
      height: 6vh;
      width: auto;
      margin-bottom: 3rem;
      user-select: none;
    }
    .top-circle-wrapper {
      display: inline-flex;
      align-items: flex-end;
      position: absolute;
      right: 5vw;
      bottom: 0;
      height: var(--icon-height, 10rem);
      aspect-ratio: 1;
      user-select: none;
      pointer-events: none;

      @include sm {
        --icon-height: 15vh;
      }
      .icon {
        height: 70%;
        width: auto;
      }
      .circle {
        position: absolute;
        right: 0;
        top: 0;
        width: 70%;
        height: auto;
        animation: spin 15s infinite linear;
      }
    }
    .flare-blue {
      --width: 70vw;
      --height: 60vw;
      --rotate: 35deg;
      animation: pulse 4s 2s infinite ease-in-out;
    }
    .flare-purple {
      --height: 80vw;
      animation: pulse 5s infinite ease-in-out;

      @include sm {
        --width: 90vh;
        --height: 100vh;
      }
    }
  }
  // &__advantages {
  // }
  &__advanced {
    .flare {
      --translate-y: 50%;
      top: unset;
      bottom: 0;
      animation: pulse 5s infinite ease-in-out;
      z-index: -1;
    }
  }
  &__gallery {
    .gallery__wrapper {
      display: flex;
      flex-direction: row;
      flex-shrink: 0;
    }
    .gallery__row {
      display: flex;
      gap: 1rem;
      flex-shrink: 0;
      padding: 0 2rem;

      &-item {
        border-radius: 20px;
        overflow: hidden;

        img {
          display: block;
          height: 80vh;
          aspect-ratio: 3/4;
          object-fit: cover;
          object-position: center center;

          @include md {
            height: auto;
            width: 80vw;
            height: 77vh;
            aspect-ratio: unset;
          }
        }
      }
    }
  }
  &__services {
    .services__wrapper {
      display: flex;
      flex-direction: row;
      flex-shrink: 0;

      .services__row {
        display: flex;
        gap: 1rem;
        flex-shrink: 0;
        padding: 0 2rem;

        &--center {
          width: 100%;
          justify-content: center;
        }
        &-item {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          align-items: flex-start;
          gap: 0.5rem;
          padding: 0.5rem;
          // max-height: 85vh;
          background-color: #f2f3f6;
          width: 25vw;
          border-radius: 1rem;

          .html__content {
            @include sm {
              margin-bottom: 2rem;
            }
          }
          .img-wrapper {
            width: 100%;
            aspect-ratio: 16/9;
            border-radius: 1rem;
            overflow: hidden;
          }
          .image {
            display: block;
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center center;
          }
          .title {
            font-size: 1rem;
          }
          .button {
            margin-top: auto;

            @include sm {
              padding: 1.65em;
              font-size: 0.75rem;
            }
          }
          @include md {
            width: 70vw;
          }
          @include sm {
            gap: 0.65rem;
          }
        }
      }
    }
  }

  .advantages__row {
    display: flex;
    flex-direction: column;
    gap: 5rem;

    &-item {
      display: flex;
      gap: 4.2rem;
      justify-content: space-between;

      .content {
        width: 55%;
        flex: 0 0 auto;
      }
      .h3,
      .h4 {
        margin-bottom: 0;
      }
      @include sm {
        flex-wrap: wrap;
        gap: 0;

        .h3,
        .h4 {
          margin-bottom: 1em;
        }
        .content {
          width: 100%;
        }
      }
    }
    @include sm {
      gap: 3rem;
    }
  }
  .advanced__row {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 1rem;
    margin-top: 5rem;

    &-item {
      background-color: #6a45ff;
      border-radius: 0.45em;
      padding: 1.125rem;

      .h4,
      p {
        color: #fff;
        font-size: 0.75rem;
      }
    }
  }

  .button {
    appearance: none;
    box-shadow: none;
    cursor: pointer;

    &--primary {
      background: linear-gradient(270deg, #6a45ff 0%, #a38cff 100%);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 60px;
      color: $white;
      font-size: 12px;
      font-weight: 400;
      padding: 2em 4em;
      margin-top: 2.745em;
      transition: opacity 0.45s ease-in-out;
      position: relative;
      overflow: hidden;
      z-index: 1;

      &:hover {
        &::before {
          opacity: 1;
        }
      }
      &::before {
        content: "";
        position: absolute;
        inset: 0;
        z-index: -1;
        opacity: 0;
        background: #6a45ff;
        transition: opacity 0.35s ease-out;
      }
      // @include sm {
      //   height: 53px;
      // }
    }
    &--large {
      width: 100%;
      font-weight: 700;
      padding: 1em;
      font-size: 16px;
      margin-top: 12vh;
      text-transform: uppercase;

      @include sm {
        font-size: 12px;
      }
    }
  }

  &__button {
    .button--large {
      margin-top: 0;
    }
  }
}
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
@keyframes pulse {
  0% {
    scale: 1;
  }
  50% {
    scale: 1.7;
  }
  100% {
    scale: 1;
  }
}
</style>
